import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Archive, Loader2, Trash2 } from 'lucide-react';
import type { MusikkTransponering } from '@/services/api';

interface SavedTranspositionsCardProps {
  savedTranspositions: MusikkTransponering[];
  loading: boolean;
  onLoad: (semitones: number) => void;
  onDelete: (id: number) => void;
}

export const SavedTranspositionsCard: React.FC<SavedTranspositionsCardProps> = ({
  savedTranspositions,
  loading,
  onLoad,
  onDelete,
}) => {
  return (
    <Card className="shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-400" />
      <div className="p-6">
        <div className="flex items-start gap-3 mb-6">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Archive className="h-5 w-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Lagrede transponeringer</h2>
                <p className="text-sm text-muted-foreground">Alle versjoner av denne sangen</p>
              </div>
              <Badge variant="secondary">{savedTranspositions.length}</Badge>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-3">
            {savedTranspositions.map((trans) => (
              <Card key={trans.id} className="p-4 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium">
                        {trans.navn}
                      </span>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {trans.semitoner > 0 ? `+${trans.semitoner}` : trans.semitoner} semitoner
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(trans.created_at).toLocaleDateString('nb-NO')}
                    </div>
                  </div>

                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onLoad(trans.semitoner)}
                    >
                      Last inn
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(trans.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}

            {savedTranspositions.length === 0 && !loading && (
              <div className="text-center py-12 text-muted-foreground">
                <p>Ingen lagrede transponeringer ennå</p>
                <p className="text-sm mt-1">
                  Velg en toneart ovenfor og klikk "Lagre transponering"
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

